import React, { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { AbridgeUniverseInterface } from '@interfaces/sell/sell.interfaces';
import { useContextSelector } from 'use-context-selector';
import { ListingContext } from '@context/listing.context';
import clsx from 'clsx';
import styles from '../../dropdown.module.scss';

interface UniversesMenuInterface {
  setCloseMenu: (close: boolean) => void;
}

const UniversesMenu: React.FC<UniversesMenuInterface> = ({ setCloseMenu }) => {
  const { universes, categories, setListingDraftAttribute, listingDraftPayload } = useContextSelector(
    ListingContext,
    (state) => state,
  );
  const { t } = useTranslation('pricing-calculator');

  const universeLabels = {
    '1': t('PRICING_CALCULATOR.UNIVERSES.WOMEN'),
    '2': t('PRICING_CALCULATOR.UNIVERSES.MEN'),
    '3': t('PRICING_CALCULATOR.UNIVERSES.GIRLS'),
    '4': t('PRICING_CALCULATOR.UNIVERSES.BOYS'),
  };

  const mappedUniverses: AbridgeUniverseInterface[] = useMemo(() => {
    return universes.map((i) => ({
      ...i,
      displayedLabel: universeLabels[i.id],
    }));
  }, [universes]);

  const getParentCategoryID = () => {
    let parentCat = '';
    categories.forEach((cat) => {
      cat.subCategories.find((subCat) => {
        if (subCat.id === listingDraftPayload.category) {
          parentCat = cat.id;
        }
      });
    });
    return parentCat;
  };

  const handleClick = (universeId: string) => {
    /**
     * If the same parent category from previous universe exists on the selected Universe,
     * and if same current subcategory exist in this parent category,
     * then display it else display generic "category" label by setting an empty string.
     */
    const categoriesFromSelectedUniverse = universes?.filter((i) => i.id === universeId)[0]?.categories;
    const matchingCatId = getParentCategoryID();
    const matchingcategoryFromSelectedUniverse = categoriesFromSelectedUniverse?.filter(
      (i) => i.id === matchingCatId,
    )[0]?.subCategories;
    const matchingSubcat = matchingcategoryFromSelectedUniverse?.find((s) => s.id === listingDraftPayload.category);
    const category = matchingSubcat ? matchingSubcat.id : '';
    setListingDraftAttribute({ universe: universeId, category });
  };

  return (
    <ul
      tabIndex={-1}
      role="listbox"
      aria-label={t('PRICING_CALCULATOR.UNIVERSES_MENU.LABEL')}
      className={styles.dropdownMenu}
      data-cy="universes-menu-list"
    >
      {mappedUniverses.map((universe) => (
        <li
          tabIndex={0}
          role="option"
          key={`universe-${universe.id}`}
          className={clsx(styles.dropdownMenu__option, {
            [styles.selected]: listingDraftPayload?.universe === universe.id,
          })}
          onClick={() => {
            handleClick(universe.id);
            setCloseMenu(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleClick(universe.id);
              setCloseMenu(true);
            }
          }}
          aria-selected={Boolean(listingDraftPayload.universe === universe.id)}
        >
          {universe.displayedLabel}
        </li>
      ))}
    </ul>
  );
};

export default UniversesMenu;
